import { Link } from 'react-router-dom'
import { AlertTriangle, Briefcase, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { formatTimeAgo } from '@/lib/format'
import type { AccountRelatedAlert, AccountRelatedCase } from '../types'

interface Props {
  alerts: AccountRelatedAlert[]
  cases: AccountRelatedCase[]
}

/**
 * AF-132 — alerts + cases involving this account.
 * Compact two-card layout; both rows deep-link into the relevant detail.
 */
export function AccountRelatedEntities({ alerts, cases }: Props) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle size={14} aria-hidden /> Related alerts ({alerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-fg">No alerts on this account.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {alerts.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/alerts/${a.id}`}
                    className="grid grid-cols-[1fr_auto_auto_1rem] items-center gap-3 rounded border border-transparent px-2 py-2 text-sm transition-colors hover:border-border hover:bg-muted"
                  >
                    <span className="flex flex-col">
                      <span className="font-medium">{a.scenario_name}</span>
                      <span className="text-xs text-muted-fg">
                        <span className="font-mono">{a.alert_number}</span> · score {a.score}
                      </span>
                    </span>
                    <Badge variant="default" className="capitalize">
                      {String(a.status).replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-muted-fg">
                      {formatTimeAgo(a.created_at)}
                    </span>
                    <ChevronRight size={12} className="text-muted-fg" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase size={14} aria-hidden /> Related cases ({cases.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cases.length === 0 ? (
            <p className="text-sm text-muted-fg">No cases involve this account.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {cases.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/cases/${c.id}`}
                    className="grid grid-cols-[1fr_auto_auto_1rem] items-center gap-3 rounded border border-transparent px-2 py-2 text-sm transition-colors hover:border-border hover:bg-muted"
                  >
                    <span className="flex flex-col">
                      <span className="font-medium">{c.title}</span>
                      <span className="text-xs text-muted-fg">
                        <span className="font-mono">{c.case_number}</span> ·{' '}
                        {c.alert_count} alert{c.alert_count === 1 ? '' : 's'}
                      </span>
                    </span>
                    <Badge variant="default" className="capitalize">
                      {String(c.status).replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-muted-fg">
                      {formatTimeAgo(c.created_at)}
                    </span>
                    <ChevronRight size={12} className="text-muted-fg" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
